import Details from '@/components/Details/Details';
import InfoLabel from '@/components/InfoLabel';
import Tip from '@/components/Tip/Tip';
import IEventType from '@/types/IEventType';
import {
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    InputGroup,
    Select,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs
} from '@chakra-ui/react';
import { FcDisclaimer } from 'react-icons/fc';
import useSectionLimits from './useSectionLimits';

export interface ISectionLimitsProps {
	service: IEventType;
	onChange: (values: Partial<IEventType>) => void;
}

export default function SectionLimits(props: ISectionLimitsProps) {
	const { t, register, handleChange, handleBlur, form, isLoading } = useSectionLimits(props);

	return (
		<Details image={<FcDisclaimer />} title={t('Booking limits')}>
			<Tabs>
				<TabList>
					<Tab>{t('Notice')}</Tab>
					<Tab>{t('Buffers')}</Tab>
					<Tab>{t('Rates')}</Tab>
					<Tab>{t('Units')}</Tab>
				</TabList>

				<TabPanels>
					<TabPanel>
						<FormControl isDisabled={isLoading} mb="1rem" id="minBookingNotice">
							<FormLabel>{t('Minimum notice period')}</FormLabel>
							<Select {...register('minBookingNotice')} onChange={handleChange}>
								<option value=""> {t('Unlimited')}</option>
								<option value="PT15M"> 15m</option>
								<option value="PT1H"> 1h</option>
								<option value="PT6H"> 6h</option>
								<option value="PT12H"> 12h</option>
								<option value="PT24H"> 24h</option>
								<option value="PT48H"> 48h</option>
							</Select>
							<FormHelperText fontSize="xs">
								{t('Minimum time between the booking date and the start of the event.')}
							</FormHelperText>
						</FormControl>

						<FormControl isDisabled={isLoading} mb="1rem" id="maxBookingNotice">
							<FormLabel>{t('Maximum notice period')}</FormLabel>
							<Select {...register('maxBookingNotice')} onChange={handleChange}>
								<option value=""> {t('Unlimited')}</option>
								<option value="PT168H"> {t('7 days')}</option>
								<option value="PT336H"> {t('14 days')}</option>
								<option value="PT720H"> {t('30 days')}</option>
								<option value="PT2160H"> {t('90 days')}</option>
							</Select>
							<FormHelperText fontSize="xs">
								{t('Maximum time between the booking date and the start of the event.')}
							</FormHelperText>
						</FormControl>
					</TabPanel>
					<TabPanel>
						<FormControl isDisabled={isLoading} mb="1rem" id="beforeBuffer">
							<FormLabel>{t('Padding time before events')}</FormLabel>
							<Select {...register('beforeBuffer')} onChange={handleChange}>
								<option value=""> {t('No buffer')}</option>
								<option value="PT5M"> 5m</option>
								<option value="PT10M"> 10m</option>
								<option value="PT15M"> 15m</option>
								<option value="PT30M"> 30m</option>
								<option value="PT1H"> 1h</option>
								<option value="PT2H"> 2h</option>
							</Select>
							<FormHelperText fontSize="xs">
								{t('Inserts a buffer of time before events for better planning.')}
							</FormHelperText>
						</FormControl>

						<FormControl isDisabled={isLoading} mb="1rem" id="afterBuffer">
							<FormLabel>{t('Padding time after events')}</FormLabel>
							<Select {...register('afterBuffer')} onChange={handleChange}>
								<option value=""> {t('No buffer')}</option>
								<option value="PT5M"> 5m</option>
								<option value="PT10M"> 10m</option>
								<option value="PT15M"> 15m</option>
								<option value="PT30M"> 30m</option>
								<option value="PT1H"> 1h</option>
								<option value="PT2H"> 2h</option>
							</Select>
							<FormHelperText fontSize="xs">
								{t('Adds extra time after events for wrap-up or transition.')}
							</FormHelperText>
						</FormControl>
					</TabPanel>
					<TabPanel>
						<FormControl isDisabled={isLoading} mb="3" id="maxUnitsPerDay.perDay">
							<FormLabel color="gray.700">{t('Maximum bookings per day')}</FormLabel>
							<InputGroup>
								<Input
									type="number"
									placeholder={t('Unlimited')}
									min="0"
									{...register('maxBookingsPerTimeUnit.perDay', { valueAsNumber: true })}
									onBlur={handleBlur}
								/>
							</InputGroup>
							<FormHelperText fontSize="xs">
								{t('The maximum number of bookings that can occur every day.')}
							</FormHelperText>
						</FormControl>

						<FormControl isDisabled={isLoading} mb="3" id="maxUnitsPerDay.perWeek">
							<FormLabel color="gray.700">{t('Maximum bookings per week')}</FormLabel>
							<InputGroup>
								<Input
									type="number"
									placeholder={t('Unlimited')}
									min="0"
									{...register('maxBookingsPerTimeUnit.perWeek', { valueAsNumber: true })}
									onBlur={handleBlur}
								/>
							</InputGroup>
							<FormHelperText fontSize="xs">
								{t('The maximum number of bookings that can occur every calendar week.')}
							</FormHelperText>
						</FormControl>

						<FormControl isDisabled={isLoading} mb="3" id="maxUnitsPerDay.perMonth">
							<FormLabel color="gray.700">{t('Maximum bookings per month')}</FormLabel>
							<InputGroup>
								<Input
									type="number"
									placeholder={t('Unlimited')}
									min="0"
									{...register('maxBookingsPerTimeUnit.perMonth', { valueAsNumber: true })}
									onBlur={handleBlur}
								/>
							</InputGroup>
							<FormHelperText fontSize="xs">
								{t('The maximum number of bookings that can occur every calendar month.')}
							</FormHelperText>
						</FormControl>

						<FormControl
							isDisabled={isLoading}
							mb="3"
							id="maxConcurrentBookings"
							isInvalid={!!form.formState.errors.maxConcurrentBookings}
						>
							<InfoLabel
								text={t('Maximum concurrent bookings')}
								help={t(
									'Activate this option to allow bookings to overlap. For example, a SPA may allow the appointment at 10:00 and the one at 10:30 to be in the pool at the same time.'
								)}
							/>

							<InputGroup>
								<Input
									type="number"
									placeholder={t('Unlimited')}
									min="1"
									{...register('maxConcurrentBookings', {
										valueAsNumber: true,
										min: {
											value: 1,
											message: t('The minimum value is 1'),
										},
									})}
									onBlur={handleBlur}
								/>
							</InputGroup>
							<FormErrorMessage>{form.formState.errors.maxConcurrentBookings?.message}</FormErrorMessage>
							<FormHelperText fontSize="xs">
								{t('The maximum number of bookings that can occur at the same time.')}
							</FormHelperText>
						</FormControl>
					</TabPanel>
					<TabPanel>
						<Tip>
							{t(
								'Units allow you to manage resources on the reservation pages. If you were a bike rental shop, you could define how many bikes are available and how many can be taken per shift.'
							)}
						</Tip>

						<FormControl mb="4" id="availableUnits" isInvalid={!!form.formState.errors.availableUnits}>
							<InfoLabel text={t('Available units')} help={t('The total number of available units')} />
							<Input
								placeholder={t('None')}
								type="number"
								{...register('availableUnits', {
									valueAsNumber: true,
									min: {
										value: 1,
										message: t('The minimum value is 1'),
									},
								})}
								onBlur={handleBlur}
							/>
							<FormErrorMessage>{form.formState.errors.availableUnits?.message}</FormErrorMessage>
						</FormControl>

						<FormControl mb="4" id="maxUnitsPerBooking" isInvalid={!!form.formState.errors.maxUnitsPerBooking}>
							<InfoLabel
								text={t('Max units per booking')}
								help={t('The maximum number of units that a customer can reserve in each slot.')}
							/>
							<Input
								placeholder={t('None')}
								type="number"
								{...register('maxUnitsPerBooking', {
									valueAsNumber: true,
									min: {
										value: 1,
										message: t('The minimum value is 1'),
									},
								})}
								onBlur={handleBlur}
							/>
							<FormErrorMessage>{form.formState.errors.maxUnitsPerBooking?.message}</FormErrorMessage>
						</FormControl>

						<FormControl mb="4" id="unitsLabel">
							<InfoLabel
								text={t('Units label')}
								help={t('Customize the text displayed for units on the booking page')}
							/>
							<Input type="text" {...register('unitsLabel')} onBlur={handleBlur} />
						</FormControl>
					</TabPanel>
				</TabPanels>
			</Tabs>
		</Details>
	);
}
